/* eslint-disable indent */
import { gql } from 'graphql-request';
import { For, Show, createEffect, createMemo, createSignal, on, onMount, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Section } from '../../grid-system/section/section';
import theme, { palette } from '../../style/theme';
import { MyContentItem, getMyContent } from './my-content-helpers';
import { ExpandableContainer } from '../../ui-components/expandable-container/expandable-container';
import { Heading } from '../../ui-components/heading/heading';
import { Text } from '../../ui-components/text/text';
import { BlogNewsData } from '../blog-news/blog-news';
import { createGroupedBlogAndNewsCard } from '../blogs-news/blogs-news-component';
import { EventData } from '../event/event-types';
import { AngleDownIcon } from '../icons-library/angle-down';
import { PagePostCard } from '../page/page-post-card';
import { ProductData } from '../product/product-types';
import { createGroupedProductCard } from '../products/products';
import { StyledShowMoreButton } from '../search/search-result.styles';
import { ShowMoreButtonProps } from '../search/search-results';
import { createGroupedVideoCard } from '../videos/videos-component';
import { VideoData } from '../videos/videos-types';
import {
    StyledCategoriesContainer,
    StyledCategoriesContainerMobile,
    StyledCategoriesHeadingDesktop,
    StyledCategoriesList,
    StyledCategoryItem,
    StyledMyContentGrid,
    StyledMyContentSection,
    StyledShowMoreButtonContainer,
} from './my-content.styles';
import { createGroupedEventCard } from '../events/helpers/events-card-helper';
import { StyledVerticalSpace } from '../../ui-components/utility-style-components/spacing';

const GET_CONTENT_QUERY = gql`
    query GetContent($key: String!) {
        resource(key: $key) {
            content
            key
        }
    }
`;

type MyContentProps = {
    lastElement?: (el: any) => void;
    filterBgColor?: string;
};

export const MyContent = (props: MyContentProps) => {
    const AppState = useContext(AppContext);
    const { graphQlClient, viewport, localize, /* pdfBuilderPage */ } = useContext(AppContext);
    const isMobile = () => viewport.width <= theme.breakpoints.MOBILE;

    const POSTS_PER_PAGE_MOBILE = 3;
    const [ currentPage, setCurrentPage ] = createSignal({
        events: 1,
        news: 1,
        products: 1,
        videos: 1,
        blogs: 1,
        pages: 1,
    });

    const [ content, setContent ] = createSignal([] as MyContentItem[]);
    const [ fetchedData, setFetchedData ] = createSignal([] as any);
    const [ loading, setLoading ] = createSignal(true);
    const [ selectedCategory, setSelectedCategory ] = createSignal<any>({
        slug: 'all',
        name: '',
    });

    const fetchCookiesAndSetContent = () => {
        setLoading(true);
        const myContent = getMyContent();
        myContent && setContent(myContent);
    };

    const fetchData = async () => {
        if (content().length === 0) {
            setLoading(false);
            return setFetchedData([]);
        }

        const promises = content().map(async (element) => {
            try {
                const response = await graphQlClient(GET_CONTENT_QUERY, { key: element.permalink }, true);
                if (!response || !response.resource) {
                    return null;
                }
                return response;
            } catch (e) {
                console.log(e);
                return null;
            }
        });

        const results = await Promise.all(promises);
        setFetchedData(results.filter(Boolean));
        setLoading(false);
    };

    onMount(() => {
        fetchCookiesAndSetContent();
        fetchData();
    });

    const refetchDataOnRemoval = () => {
        fetchCookiesAndSetContent();
        fetchData();
    };

    createEffect(
        on(
            () => content(),
            () => fetchData(),
            {
                defer: true,
            }
        )
    );

    const fetchedContent = createMemo((): any => fetchedData().map((element: any) => element.resource.content));

    const events = createMemo((): any => fetchedContent().filter((item: any) => item.type === 'event'));
    const news = createMemo((): any => fetchedContent().filter((item: any) => item.type === 'blog-and-news' && item.tags.includes('news')));
    const products = createMemo((): any => fetchedContent().filter((item: any) => item.type === 'product'));
    const videos = createMemo((): any => fetchedContent().filter((item: any) => item.type === 'video-item'));
    const blogs = createMemo((): any => fetchedContent().filter((item: any) => item.type === 'blog-and-news' && !item.tags.includes('news')));
    const pages = createMemo((): any => fetchedContent().filter((item: any) => item.type === 'page'));

    const categories = [
        { name: 'Events', fn: events, slug: 'events' },
        { name: 'News', fn: news, slug: 'news' },
        { name: 'Products', fn: products, slug: 'products' },
        { name: 'Videos', fn: videos, slug: 'videos' },
        { name: 'Blogs', fn: blogs, slug: 'blogs' },
        { name: 'Pages', fn: pages, slug: 'pages' },
    ];

    const nonEmptyCategories = () => categories.filter(({ fn }) => fn().find(() => true));

    const categoryName = () => {
        const category = categories.find((category) => {
            return category.slug === selectedCategory().slug;
        });

        if (!category) {
            return;
        }

        return category.name;
    };

    let expandableContainer: HTMLDivElement | undefined;
    const handleCategoryClick = (category: any) => {
        const categoryToUse = category || { slug: 'all', name: localize('all', 'All') };
        setSelectedCategory(categoryToUse);
        expandableContainer?.click();
    };

    const renderCategoriesList = () => {
        return (
            <StyledCategoriesList>
                <StyledCategoryItem
                    underlined={selectedCategory().slug === 'all'}
                    onClick={() => handleCategoryClick(null)}
                    aria-label={localize('show-all-categories', 'Show All Categories')}
                >
                    <Heading tag="h3" variant="tinyRed" noBlockSpacing={true}>
                        {localize('all', 'All')}
                    </Heading>
                </StyledCategoryItem>

                <For each={nonEmptyCategories()}>
                    {(topCategory: any) => (
                        <li style={{ 'padding-bottom': 0 }}>
                            <StyledCategoryItem
                                underlined={selectedCategory()?.slug === topCategory.slug}
                                onClick={() => handleCategoryClick(topCategory)}
                                aria-label={localize('show-category', 'Show Category')}
                            >
                                <Heading tag="h3" variant="tinyRed" noBlockSpacing={true}>
                                    {topCategory.name}
                                </Heading>
                            </StyledCategoryItem>
                        </li>
                    )}
                </For>
            </StyledCategoriesList>
        );
    };

    const ShowMoreButton = (props: ShowMoreButtonProps) => {
        return (
            <Show
                when={
                    Math.min(props.currentPage * props.resultsPerPage, props.totalResults) !== props.totalResults &&
                    AppState.viewport.width <= theme.breakpoints.MOBILE
                }
            >
                <StyledShowMoreButtonContainer>
                    <StyledShowMoreButton type="button" onClick={props.onClick}>
                        <div>{localize('show-more', 'show more')}</div>
                        <AngleDownIcon fill={theme.palette.primary} {...{ height: 2.14, width: 2.86 }} ariaHidden />
                    </StyledShowMoreButton>
                </StyledShowMoreButtonContainer>
            </Show>
        );
    };

    const CategoriesMobile = () => (
        <StyledCategoriesContainerMobile>
            <ExpandableContainer
                ref={expandableContainer}
                initialOpen={false}
                chevronColor={palette.primary}
                isDropdown={true}
                shrinkOnTablet={true}
                withBorder={true}
                headingProps={{
                    noBlockSpacing: true,
                    tag: 'h3',
                    variant: 'medium',
                    children: categoryName() || localize('categories', 'Categories'),
                }}
            >
                {renderCategoriesList()}
            </ExpandableContainer>
        </StyledCategoriesContainerMobile>
    );

    const Events = () => (
        <Show when={(selectedCategory().slug === 'all' || selectedCategory().slug === 'events') && events().length}>
            <StyledMyContentSection>
                <Heading tag="h3" variant="large">
                    {localize('events', 'Events')}
                </Heading>

                <StyledMyContentGrid>
                    <For each={isMobile() ? events().slice(0, currentPage().events * POSTS_PER_PAGE_MOBILE) : events()} fallback={null}>
                        {(event: EventData) => (
                            <div onClick={() => refetchDataOnRemoval()} style={{ 'z-index': 0 }}>
                                {createGroupedEventCard({ event })}
                            </div>
                        )}
                    </For>
                </StyledMyContentGrid>

                <ShowMoreButton
                    currentPage={currentPage().events}
                    resultsPerPage={POSTS_PER_PAGE_MOBILE}
                    totalResults={events().length}
                    onClick={() => setCurrentPage((prevPage) => ({ ...prevPage, events: prevPage.events + 1 }))}
                />
            </StyledMyContentSection>
        </Show>
    );

    const News = () => (
        <Show when={(selectedCategory().slug === 'all' || selectedCategory().slug === 'news') && news().length}>
            <StyledMyContentSection>
                <Heading tag="h3" variant="large">
                    {localize('news', 'News')}
                </Heading>

                <StyledMyContentGrid>
                    <For each={isMobile() ? news().slice(0, currentPage().news * POSTS_PER_PAGE_MOBILE) : news()} fallback={null}>
                        {(post: BlogNewsData) => (
                            <div onClick={() => refetchDataOnRemoval()} style={{ 'z-index': 0 }}>
                                {createGroupedBlogAndNewsCard({ post })}
                            </div>
                        )}
                    </For>
                </StyledMyContentGrid>

                <ShowMoreButton
                    currentPage={currentPage().news}
                    resultsPerPage={POSTS_PER_PAGE_MOBILE}
                    totalResults={news().length}
                    onClick={() => setCurrentPage((prevPage) => ({ ...prevPage, news: prevPage.news + 1 }))}
                />
            </StyledMyContentSection>
        </Show>
    );

    const Products = () => (
        <Show when={(selectedCategory().slug === 'all' || selectedCategory().slug === 'products') && products().length}>
            <StyledMyContentSection>
                <Heading tag="h3" variant="large">
                    {localize('products', 'Products')}
                </Heading>
                <StyledMyContentGrid>
                    <For each={isMobile() ? products().slice(0, currentPage().products * POSTS_PER_PAGE_MOBILE) : products()} fallback={null}>
                        {(product: ProductData) => (
                            <div onClick={() => refetchDataOnRemoval()}>
                                {createGroupedProductCard(product)}
                            </div>
                        )}
                    </For>
                </StyledMyContentGrid>

                <ShowMoreButton
                    currentPage={currentPage().products}
                    resultsPerPage={POSTS_PER_PAGE_MOBILE}
                    totalResults={products().length}
                    onClick={() => setCurrentPage((prevPage) => ({ ...prevPage, products: prevPage.products + 1 }))}
                />
            </StyledMyContentSection>
        </Show>
    );

    const Videos = () => (
        <Show when={(selectedCategory().slug === 'all' || selectedCategory().slug === 'videos') && videos().length}>
            <StyledMyContentSection>
                <Heading tag="h3" variant="large">
                    {localize('videos', 'Videos')}
                </Heading>
                <StyledMyContentGrid>
                    <For each={isMobile() ? videos().slice(0, currentPage().videos * POSTS_PER_PAGE_MOBILE) : videos()} fallback={null}>
                        {(video: VideoData) => (
                            <div onClick={() => refetchDataOnRemoval()}>
                                {createGroupedVideoCard(video, true)}
                            </div>
                        )}
                    </For>
                </StyledMyContentGrid>

                <ShowMoreButton
                    currentPage={currentPage().videos}
                    resultsPerPage={POSTS_PER_PAGE_MOBILE}
                    totalResults={videos().length}
                    onClick={() => setCurrentPage((prevPage) => ({ ...prevPage, videos: prevPage.videos + 1 }))}
                />
            </StyledMyContentSection>
        </Show>
    );

    const Blogs = () => (
        <Show when={(selectedCategory().slug === 'all' || selectedCategory().slug === 'blogs') && blogs().length}>
            <StyledMyContentSection>
                <Heading tag="h3" variant="large">
                    {localize('blogs', 'Blogs')}
                </Heading>
                <StyledMyContentGrid>
                    <For each={isMobile() ? blogs().slice(0, currentPage().blogs * POSTS_PER_PAGE_MOBILE) : blogs()} fallback={null}>
                        {(post: BlogNewsData) => (
                            <div onClick={() => refetchDataOnRemoval()} style={{ 'z-index': 0 }}>
                                {createGroupedBlogAndNewsCard({ post })}
                            </div>
                        )}
                    </For>
                </StyledMyContentGrid>

                <ShowMoreButton
                    currentPage={currentPage().blogs}
                    resultsPerPage={POSTS_PER_PAGE_MOBILE}
                    totalResults={blogs().length}
                    onClick={() => setCurrentPage((prevPage) => ({ ...prevPage, blogs: prevPage.blogs + 1 }))}
                />
            </StyledMyContentSection>
        </Show>
    );

    const Pages = () => (
        <Show when={(selectedCategory().slug === 'all' || selectedCategory().slug === 'pages') && pages().length}>
            <StyledMyContentSection>
                <Heading tag="h3" variant="large">
                    {localize('pages', 'Pages')}
                </Heading>
                {/* TODO: maybe in future we would want to use createGroupedPageCard method but as for now i dont see reason for that */}
                <StyledMyContentGrid>
                    <For each={isMobile() ? pages().slice(0, currentPage().pages * POSTS_PER_PAGE_MOBILE) : pages()} fallback={null}>
                        {(post: any) => (
                            <div onClick={() => refetchDataOnRemoval()}>
                                <PagePostCard data={post} />
                            </div>
                        )}
                    </For>
                </StyledMyContentGrid>

                <ShowMoreButton
                    currentPage={currentPage().pages}
                    resultsPerPage={POSTS_PER_PAGE_MOBILE}
                    totalResults={pages().length}
                    onClick={() => setCurrentPage((prevPage) => ({ ...prevPage, pages: prevPage.pages + 1 }))}
                />
            </StyledMyContentSection>
        </Show>
    );

    const DummyButtonForTabbing = () => (
        // We need something to focus as the last element (the alternative is to figure out the last element in the list of lists - not worth it)
        <button
            style={{ width: 0, height: 0, opacity: 0 }}
            onClick={() => {}}
            ref={(el: HTMLElement) => props.lastElement && props.lastElement(el)}
            aria-hidden="true"
        />
    );

    return (
        <>
            <Heading tag='h2' noBlockSpacing={isMobile()}>
                {localize('bookmarks', 'Bookmarks')}
            </Heading>

            <Show when={!isMobile()}>
                <StyledVerticalSpace size={1} />
            </Show>

            <Show when={!isMobile()} fallback={<CategoriesMobile />}>
                <StyledCategoriesContainer filterBgColor={props.filterBgColor}>
                    <StyledCategoriesHeadingDesktop>
                        <Heading tag="h3" variant="medium" noBlockSpacing={true}>
                            {localize('filters', 'Filters')}
                        </Heading>
                    </StyledCategoriesHeadingDesktop>
                    {renderCategoriesList()}
                </StyledCategoriesContainer>
            </Show>

            <Show when={loading()}>
                <StyledMyContentSection>
                    <StyledVerticalSpace size={2} />
                    <Heading align={'center'} tag='h4'>{localize('loading', 'Loading...')}</Heading>
                </StyledMyContentSection>
            </Show>

            <Show when={!loading() && !nonEmptyCategories().length}>
                <Section templateShorthand={[ 12 ]} widthType="bgFull" isLayoutSection={true}>
                    <Text fontSize='normal'>{localize('no-results', 'No results found')}</Text>
                </Section>
            </Show>

            <StyledVerticalSpace size={2} />

            <Events />
            <News />
            <Products />
            <Videos />
            <Blogs />
            <Pages />

            <DummyButtonForTabbing />
        </>
    );
};
